/*
 * KcViewer.jsx - KC Test Bench result screen (Ballooning-style viewer).
 * One self-contained React component. No external UI libraries (inline SVG icons, Tailwind classes only).
 *
 * Features: zoom / pan / fit / rotate, orange detected boxes + numbered balloons with leaders,
 * drag balloons, add manual balloon, renumber, sort, contrast, re-detect, EYE mode (white-mask detected
 * items, box missed text -> OCR -> pending -> Finish/Cancel), right panel with modes
 * (Ballooning / Title Block / Reference Grid / Dimension Groups), Download PDF, Inspection Sheet (xlsx/csv/pdf),
 * tabs Dims / Views / Tols, units + convert, verified progress, filter, Inspection Plan drawer, autosave.
 *
 * Props:
 *   docId    : string
 *   api      : optional object overriding the default REST calls (see defaultApi below)
 *   apiBase  : default "/api"
 *   onBack   : () => void   (Home button)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* ------------------------------------------------------------------ default REST contract */
function defaultApi(apiBase) {
  const j = async (r) => {
    if (!r.ok) throw new Error((await r.text()) || r.statusText);
    return r.json();
  };
  const opt = (method, body) => ({
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  return {
    getDoc: (id) => fetch(`${apiBase}/kc/docs/${id}`, opt("GET")).then(j),
    saveDoc: (id, patch) => fetch(`${apiBase}/kc/docs/${id}`, opt("PUT", patch)).then(j),
    readRegion: (id, bbox) => fetch(`${apiBase}/kc/docs/${id}/read-region`, opt("POST", { bbox, angle: "auto" })).then(j),
    titleBlock: (id, bbox) => fetch(`${apiBase}/kc/docs/${id}/title-block`, opt("POST", { bbox })).then(j),
    rerun: (id) => fetch(`${apiBase}/kc/docs/${id}/rerun`, opt("POST", {})).then(j),
    imageUrl: (id) => `${apiBase}/kc/docs/${id}/image`,
    pdfUrl: (id) => `${apiBase}/kc/docs/${id}/ballooned.pdf`,
    sheetUrl: (id, fmt) => `${apiBase}/kc/docs/${id}/inspection.${fmt}`,
  };
}

/* ------------------------------------------------------------------ icons (inline svg) */
const P = {
  grip: "M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01",
  box: "M4 4h4M4 4v4M20 4h-4M20 4v4M4 20h4M4 20v-4M20 20h-4M20 20v-4M9 9h6v6H9z",
  pencil: "M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4",
  sort: "M7 4v16M4 17l3 3 3-3M14 6h6M14 12h4M14 18h2",
  rotate: "M4 12a8 8 0 0 1 14-5.3M20 4v5h-5M20 12a8 8 0 0 1-14 5.3M4 20v-5h5",
  eye: "M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  eyeOff: "M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-3 3.7M6.6 6.6A17 17 0 0 0 2 12s4 7 10 7a9.8 9.8 0 0 0 5.4-1.6M9.9 9.9a3 3 0 0 0 4.2 4.2",
  detect: "M12 3v3M12 18v3M3 12h3M18 12h3M7 7l-1.5-1.5M17 7l1.5-1.5M7 17l-1.5 1.5M17 17l1.5 1.5M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
  download: "M12 4v11M7 10l5 5 5-5M5 20h14",
  sheet: "M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h7",
  chevron: "M6 9l6 6 6-6",
  gear: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z",
  check: "M5 12l5 5L20 7",
  alert: "M12 8v5M12 16h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z",
  x: "M6 6l12 12M18 6L6 18",
  plus: "M12 5v14M5 12h14",
  minus: "M5 12h14",
  fit: "M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5",
  home: "M15 18l-6-6 6-6",
  filter: "M4 6h16M7 12h10M10 18h4",
  grid: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  title: "M4 5h16v14H4zM4 10h16M9 10v9",
  group: "M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM16 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 20a5 5 0 0 1 10 0M12 20a5 5 0 0 1 9 0",
  balloon: "M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM12 8v8M9 9.5l3-1.5",
  plan: "M8 3h8v3H8zM6 5H5v16h14V5h-1M9 12h6M9 16h6",
  trash: "M5 7h14M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3",
  lock: "M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4",
  num: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM9.5 15.5v-7l5 7v-7",
  contrast: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 3v18",
};
function Icon({ d, size = 18, className = "", stroke = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d={P[d]} />
    </svg>
  );
}

/* ------------------------------------------------------------------ helpers */
const uid = () => Math.random().toString(36).slice(2, 10);
const GROUP_COLORS = ["#2563eb", "#16a34a", "#9333ea", "#ea580c", "#0891b2", "#db2777", "#65a30d", "#b45309"];
const fmtNum = (v, units) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const x = units === "inch" ? n / 25.4 : n;
  return units === "inch" ? x.toFixed(4).replace(/0+$/, "").replace(/\.$/, "") : String(Math.round(x * 10000) / 10000);
};
const center = (b) => [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
const inside = (pt, b) => pt[0] >= b[0] && pt[0] <= b[2] && pt[1] >= b[1] && pt[1] <= b[3];
const rowLetter = (start, i) => {
  const base = (start || "A").toUpperCase().charCodeAt(0) - 65;
  let n = base + i, s = "";
  do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
  return s;
};
function nearestOnBox(px, py, b) {
  return [Math.min(Math.max(px, b[0]), b[2]), Math.min(Math.max(py, b[1]), b[3])];
}
function readingOrder(chars, views) {
  // views (top band first, then left to right), inside a view rows of ~balloon height
  const vIndex = (c) => {
    const i = views.findIndex((v) => v.name === c.view);
    return i < 0 ? 999 : i;
  };
  return [...chars].sort((a, b) => {
    const va = vIndex(a), vb = vIndex(b);
    if (va !== vb) return va - vb;
    const [ax, ay] = center(a.bbox), [bx, by] = center(b.bbox);
    const band = Math.max(20, ((a.bbox[3] - a.bbox[1]) + (b.bbox[3] - b.bbox[1])));
    if (Math.abs(ay - by) > band) return ay - by;
    return ax - bx;
  });
}

/* ------------------------------------------------------------------ main component */
export default function KcViewer({ docId, api: apiProp, apiBase = "/api", onBack }) {
  const api = useMemo(() => ({ ...defaultApi(apiBase), ...(apiProp || {}) }), [apiProp, apiBase]);

  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [chars, setChars] = useState([]);
  const [views, setViews] = useState([]);
  const [groups, setGroups] = useState([]);
  const [grid, setGrid] = useState(null);           // {box:[x0,y0,x1,y1], xs:[...], ys:[...], rowStart, colStart}
  const [titleBlock, setTitleBlock] = useState(null); // {bbox, fields:[{key,label,value,checked}]}
  const [units, setUnits] = useState("mm");
  const [convert, setConvert] = useState(false);
  const [generalTol, setGeneralTol] = useState("");

  const [mode, setMode] = useState("balloon");      // balloon | title | grid | groups
  const [modeOpen, setModeOpen] = useState(false);
  const [tab, setTab] = useState("dims");           // dims | views | tols
  const [tool, setTool] = useState("select");       // select | add | view | title | eye
  const [eyeOn, setEyeOn] = useState(false);
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selView, setSelView] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [contrast, setContrast] = useState(false);
  const [showBoxes, setShowBoxes] = useState(true);
  const [balloonScale, setBalloonScale] = useState(1);
  const [editing, setEditing] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const [busy, setBusy] = useState("");

  const [zoom, setZoom] = useState(0.2);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rot, setRot] = useState(0);
  const [rubber, setRubber] = useState(null);       // {x0,y0,x1,y1} image px while dragging
  const [dock, setDock] = useState({ x: null, y: null });

  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const loadedRef = useRef(false);

  /* ---------- load */
  const load = useCallback(async () => {
    try {
      const d = await api.getDoc(docId);
      setDoc(d);
      setChars((d.characteristics || []).map((c, i) => ({
        id: c.id || uid(), no: c.no ?? i + 1, verified: !!c.verified, ...c,
        bbox: c.bbox || [0, 0, 10, 10],
      })));
      setViews((d.views || []).map((v, i) => ({ id: v.id || uid(), name: v.name || `View #${i + 1}`, bbox: v.bbox })));
      setGroups(d.groups || []);
      setGrid(d.grid || null);
      setTitleBlock(d.title_block || null);
      setUnits(d.units || "mm");
      setGeneralTol(d.general_tolerance || "");
      loadedRef.current = true;
    } catch (e) {
      setErr(String(e.message || e));
    }
  }, [api, docId]);
  useEffect(() => { load(); }, [load]);

  const W = doc?.image_w || 1000, H = doc?.image_h || 700;
  const pxPerMm = doc?.px_per_mm || W / 420;
  const R = (doc?.balloon_radius || Math.max(14, 0.011 * Math.max(W, H))) * balloonScale;

  /* ---------- autosave */
  useEffect(() => {
    if (!loadedRef.current) return;
    setSaveState("dirty");
    const t = setTimeout(async () => {
      try {
        setSaveState("saving");
        await api.saveDoc(docId, {
          characteristics: chars, views, groups, grid, title_block: titleBlock,
          units, general_tolerance: generalTol,
        });
        setSaveState("saved");
      } catch (e) {
        setSaveState("error");
      }
    }, 800);
    return () => clearTimeout(t);
  }, [chars, views, groups, grid, titleBlock, units, generalTol]); // eslint-disable-line

  /* ---------- fit / zoom */
  const fit = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const cw = el.clientWidth, ch = el.clientHeight;
    const rw = rot % 180 === 0 ? W : H, rh = rot % 180 === 0 ? H : W;
    const z = Math.min(cw / rw, ch / rh) * 0.94;
    setZoom(z);
    setPan({ x: (cw - W * z) / 2, y: (ch - H * z) / 2 });
  }, [W, H, rot]);
  useEffect(() => { if (doc) fit(); }, [doc, rot]); // eslint-disable-line

  const zoomAt = (factor, cx, cy) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = cx === undefined ? r.width / 2 : cx - r.left;
    const py = cy === undefined ? r.height / 2 : cy - r.top;
    setZoom((z) => {
      const nz = Math.min(8, Math.max(0.03, z * factor));
      setPan((p) => ({ x: px - (px - p.x) * (nz / z), y: py - (py - p.y) * (nz / z) }));
      return nz;
    });
  };
  const onWheel = (e) => { e.preventDefault(); zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY); };
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  /* ---------- coordinates: client -> image px (handles zoom, pan, rotation) */
  const toImg = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return [p.x, p.y];
  };

  /* ---------- derived */
  const numbered = useMemo(() => [...chars].sort((a, b) => a.no - b.no), [chars]);
  const verifiedCount = chars.filter((c) => c.verified).length;
  const gridCell = useCallback((c) => {
    if (!grid || !grid.xs?.length || !grid.ys?.length) return "";
    const [x, y] = center(c.bbox);
    const xs = [grid.box[0], ...grid.xs, grid.box[2]].sort((a, b) => a - b);
    const ys = [grid.box[1], ...grid.ys, grid.box[3]].sort((a, b) => a - b);
    let ci = xs.findIndex((v, i) => i < xs.length - 1 && x >= v && x <= xs[i + 1]);
    let ri = ys.findIndex((v, i) => i < ys.length - 1 && y >= v && y <= ys[i + 1]);
    if (ci < 0 || ri < 0) return "";
    return `${rowLetter(grid.rowStart, ri)}${(Number(grid.colStart) || 1) + ci}`;
  }, [grid]);
  const visibleRows = useMemo(() => numbered.filter((c) => {
    if (filter === "review") return c.check && !c.verified;
    if (filter === "verified") return c.verified;
    if (filter === "unverified") return !c.verified;
    if (filter === "gdt") return c.type === "gdt";
    if (filter === "basic") return c.basic;
    return true;
  }), [numbered, filter]);

  const update = (id, patch) => setChars((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const remove = (id) => setChars((cs) => cs.filter((c) => c.id !== id));
  const renumber = (order) => setChars((order || numbered).map((c, i) => ({ ...c, no: i + 1 })));
  const sortAndNumber = () => renumber(readingOrder(chars, views));
  const nextNo = () => chars.reduce((m, c) => Math.max(m, c.no || 0), 0) + 1;

  const focusChar = (c) => {
    setSelected(c.id);
    const el = wrapRef.current;
    if (!el) return;
    const [cx, cy] = center(c.bbox);
    setPan({ x: el.clientWidth / 2 - cx * zoom, y: el.clientHeight / 2 - cy * zoom });
  };

  /* ---------- eye mode */
  const eyeStart = () => { setEyeOn(true); setTool("eye"); setPending([]); };
  const eyeCancel = () => { setEyeOn(false); setTool("select"); setPending([]); };
  const eyeFinish = () => {
    let n = nextNo();
    const add = pending.filter((p) => (p.text || "").trim()).map((p) => {
      const parsed = p.parsed || {};
      return {
        id: uid(), no: n++, text: p.text.trim(), bbox: p.bbox,
        bx: p.bbox[2] + R * 1.6, by: p.bbox[1] - R * 0.6,
        type: parsed.type || "note", nominal: parsed.nominal ?? null, plus: parsed.plus ?? null,
        minus: parsed.minus ?? null, lower: parsed.lower ?? null, upper: parsed.upper ?? null,
        qty: parsed.qty ?? null, reference: !!parsed.reference, basic: false, gdt: parsed.gdt || "",
        datums: parsed.datums || "", unit: parsed.unit || "mm", check: "added manually (eye)",
        verified: false, source: "manual-ocr",
        view: (views.find((v) => v.bbox && inside(center(p.bbox), v.bbox)) || {}).name || "",
      };
    });
    setChars((cs) => [...cs, ...add]);
    eyeCancel();
  };
  const readBox = async (bbox) => {
    const id = uid();
    setPending((ps) => [...ps, { id, bbox, text: "", loading: true }]);
    try {
      const r = await api.readRegion(docId, bbox.map((v) => Math.round(v)));
      setPending((ps) => ps.map((p) => (p.id === id ? { ...p, loading: false, text: r.text || "", parsed: r.parsed || r } : p)));
    } catch (e) {
      setPending((ps) => ps.map((p) => (p.id === id ? { ...p, loading: false, text: "", error: String(e.message || e) } : p)));
    }
  };

  /* ---------- title block */
  const pickTitle = async (bbox) => {
    setBusy("Reading title block...");
    try {
      const r = await api.titleBlock(docId, bbox.map((v) => Math.round(v)));
      setTitleBlock({ bbox, fields: (r.fields || []).map((f) => ({ checked: true, ...f })) });
    } catch (e) {
      setTitleBlock({ bbox, fields: [], error: String(e.message || e) });
    }
    setBusy("");
  };

  /* ---------- grid */
  const autoGrid = () => {
    const fb = doc?.frame_bbox || [W * 0.03, H * 0.04, W * 0.97, H * 0.96];
    const wmm = (fb[2] - fb[0]) / pxPerMm, hmm = (fb[3] - fb[1]) / pxPerMm;
    const cols = Math.min(16, Math.max(2, Math.round(wmm / 52)));
    const rows = Math.min(12, Math.max(2, Math.round(hmm / 52)));
    setGrid({
      box: fb, rowStart: "A", colStart: 1,
      xs: Array.from({ length: cols - 1 }, (_, i) => fb[0] + ((i + 1) * (fb[2] - fb[0])) / cols),
      ys: Array.from({ length: rows - 1 }, (_, i) => fb[1] + ((i + 1) * (fb[3] - fb[1])) / rows),
      locked: [],
    });
  };
  const setGridCount = (axis, n) => {
    if (!grid) return;
    n = Math.min(26, Math.max(1, Number(n) || 1));
    const b = grid.box;
    const span = axis === "xs" ? b[2] - b[0] : b[3] - b[1];
    const start = axis === "xs" ? b[0] : b[1];
    setGrid({ ...grid, [axis]: Array.from({ length: n - 1 }, (_, i) => start + ((i + 1) * span) / n) });
  };

  /* ---------- mouse on stage */
  const onDown = (e) => {
    if (e.button !== 0) return;
    const [x, y] = toImg(e.clientX, e.clientY);
    const bid = e.target?.dataset?.bid;
    const gl = e.target?.dataset?.gline;
    if (gl && mode === "grid" && grid) {
      const [axis, idx] = gl.split(":");
      dragRef.current = { kind: "gline", axis, idx: Number(idx) };
      return;
    }
    if (bid && tool === "select" && !eyeOn) {
      setSelected(bid);
      dragRef.current = { kind: "balloon", id: bid, ox: x, oy: y };
      return;
    }
    if (tool === "add" && !eyeOn) {
      const c = {
        id: uid(), no: nextNo(), text: "", bbox: [x - 30, y - 15, x + 30, y + 15], bx: x + R * 2, by: y - R * 2,
        type: "note", check: "manual - enter text", verified: false, source: "manual",
        view: (views.find((v) => v.bbox && inside([x, y], v.bbox)) || {}).name || "",
      };
      setChars((cs) => [...cs, c]);
      setSelected(c.id);
      setEditing(c.id);
      setTab("dims");
      setTool("select");
      return;
    }
    if (eyeOn || tool === "view" || tool === "title") {
      dragRef.current = { kind: "rubber", x0: x, y0: y };
      setRubber({ x0: x, y0: y, x1: x, y1: y });
      return;
    }
    dragRef.current = { kind: "pan", sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y };
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.kind === "pan") {
      setPan({ x: d.px + (e.clientX - d.sx), y: d.py + (e.clientY - d.sy) });
      return;
    }
    const [x, y] = toImg(e.clientX, e.clientY);
    if (d.kind === "balloon") update(d.id, { bx: x, by: y });
    else if (d.kind === "rubber") setRubber({ x0: d.x0, y0: d.y0, x1: x, y1: y });
    else if (d.kind === "gline") {
      setGrid((g) => {
        if (!g || (g.locked || []).includes(`${d.axis}:${d.idx}`)) return g;
        const arr = [...g[d.axis]];
        arr[d.idx] = d.axis === "xs" ? x : y;
        return { ...g, [d.axis]: arr };
      });
    }
  };
  const onUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || d.kind !== "rubber" || !rubber) return;
    const b = [Math.min(rubber.x0, rubber.x1), Math.min(rubber.y0, rubber.y1), Math.max(rubber.x0, rubber.x1), Math.max(rubber.y0, rubber.y1)];
    setRubber(null);
    if (b[2] - b[0] < 6 || b[3] - b[1] < 6) return;
    if (eyeOn) readBox(b);
    else if (tool === "view") {
      const v = { id: uid(), name: `View #${views.length + 1}`, bbox: b };
      setViews((vs) => [...vs, v]);
      setChars((cs) => cs.map((c) => (inside(center(c.bbox), b) ? { ...c, view: v.name } : c)));
      setTool("select");
      setTab("views");
    } else if (tool === "title") {
      pickTitle(b);
      setTool("select");
    }
  };

  /* ---------- keyboard */
  useEffect(() => {
    const k = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (e.key === "h" || e.key === "H") { eyeOn ? eyeCancel() : eyeStart(); }
      else if (e.key === "Escape") { if (eyeOn) eyeCancel(); else { setTool("select"); setRubber(null); } }
      else if (e.key === "Delete" && selected) remove(selected);
      else if (e.key === "+" || e.key === "=") zoomAt(1.2);
      else if (e.key === "-") zoomAt(1 / 1.2);
      else if (e.key === "f" || e.key === "F") fit();
      else if (e.key === "a" || e.key === "A") setTool("add");
      else if (e.key === "v" || e.key === "V") setTool("select");
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });

  /* ---------- dock drag */
  const dockDown = (e) => {
    e.stopPropagation();
    const r = e.currentTarget.parentElement.getBoundingClientRect();
    const wr = wrapRef.current.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, ox = r.left - wr.left, oy = r.top - wr.top;
    const mv = (ev) => setDock({ x: ox + ev.clientX - sx, y: oy + ev.clientY - sy });
    const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
  };

  if (err) return <div className="p-8 text-red-600">Could not load drawing: {err}</div>;
  if (!doc) return <div className="p-8 text-gray-500">Loading drawing...</div>;

  const groupColor = (c) => (mode === "groups" && c.group ? (groups.find((g) => g.id === c.group) || {}).color : null);
  const stageTransform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) translate(${W / 2}px, ${H / 2}px) rotate(${rot}deg) translate(${-W / 2}px, ${-H / 2}px)`;
  const strokeW = Math.max(1.2, R * 0.12);

  /* ================================================================== render */
  return (
    <div className="flex h-full min-h-[600px] w-full bg-gray-50 text-gray-800 select-none">
      {/* ------------------------------------------------ canvas */}
      <div className="relative flex-1 overflow-hidden" ref={wrapRef}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        style={{ cursor: eyeOn || tool === "view" || tool === "title" ? "crosshair" : tool === "add" ? "copy" : dragRef.current ? "grabbing" : "grab" }}>
        <div style={{ position: "absolute", left: 0, top: 0, width: W, height: H, transformOrigin: "0 0", transform: stageTransform }}>
          <img src={api.imageUrl(docId)} alt="drawing" draggable={false} width={W} height={H}
            style={{ width: W, height: H, filter: contrast ? "contrast(1.9) brightness(1.05) grayscale(1)" : "none", pointerEvents: "none" }} />
          <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: "absolute", left: 0, top: 0, overflow: "visible" }}>
            {/* views */}
            {tab === "views" && mode === "balloon" && !eyeOn && views.map((v) => v.bbox && (
              <rect key={v.id} x={v.bbox[0]} y={v.bbox[1]} width={v.bbox[2] - v.bbox[0]} height={v.bbox[3] - v.bbox[1]}
                fill={selView === v.id ? "rgba(37,99,235,0.08)" : "rgba(107,114,128,0.10)"}
                stroke={selView === v.id ? "#2563eb" : "#9ca3af"} strokeWidth={strokeW * 1.5} />
            ))}
            {/* title block region */}
            {mode === "title" && titleBlock?.bbox && (
              <rect x={titleBlock.bbox[0]} y={titleBlock.bbox[1]} width={titleBlock.bbox[2] - titleBlock.bbox[0]}
                height={titleBlock.bbox[3] - titleBlock.bbox[1]} fill="rgba(37,99,235,0.06)" stroke="#2563eb" strokeWidth={strokeW * 2} />
            )}
            {/* reference grid */}
            {grid && (mode === "grid" || grid.show) && (
              <g>
                {grid.xs.map((x, i) => (
                  <g key={"x" + i}>
                    <line x1={x} y1={grid.box[1]} x2={x} y2={grid.box[3]} stroke="#94a3b8" strokeWidth={strokeW} />
                    {mode === "grid" && <line data-gline={`xs:${i}`} x1={x} y1={grid.box[1]} x2={x} y2={grid.box[3]} stroke="transparent" strokeWidth={strokeW * 10} style={{ cursor: "ew-resize" }} />}
                  </g>
                ))}
                {grid.ys.map((y, i) => (
                  <g key={"y" + i}>
                    <line x1={grid.box[0]} y1={y} x2={grid.box[2]} y2={y} stroke="#94a3b8" strokeWidth={strokeW} />
                    {mode === "grid" && <line data-gline={`ys:${i}`} x1={grid.box[0]} y1={y} x2={grid.box[2]} y2={y} stroke="transparent" strokeWidth={strokeW * 10} style={{ cursor: "ns-resize" }} />}
                  </g>
                ))}
                {mode === "grid" && (() => {
                  const xs = [grid.box[0], ...grid.xs, grid.box[2]].sort((a, b) => a - b);
                  const ys = [grid.box[1], ...grid.ys, grid.box[3]].sort((a, b) => a - b);
                  const fs = R * 1.3;
                  return (
                    <g fontSize={fs} fontWeight="700" fill="#1f2937">
                      {xs.slice(0, -1).map((x, i) => <text key={"cx" + i} x={(x + xs[i + 1]) / 2} y={grid.box[1] - fs * 0.5} textAnchor="middle">{(Number(grid.colStart) || 1) + i}</text>)}
                      {ys.slice(0, -1).map((y, i) => <text key={"ry" + i} x={grid.box[0] - fs * 0.9} y={(y + ys[i + 1]) / 2 + fs * 0.35} textAnchor="middle">{rowLetter(grid.rowStart, i)}</text>)}
                    </g>
                  );
                })()}
              </g>
            )}
            {/* eye mask: white over every detected item */}
            {eyeOn && chars.map((c) => (
              <rect key={"m" + c.id} x={c.bbox[0] - 4} y={c.bbox[1] - 4} width={c.bbox[2] - c.bbox[0] + 8} height={c.bbox[3] - c.bbox[1] + 8} fill="#ffffff" />
            ))}
            {/* detected boxes, leaders, balloons */}
            {!eyeOn && showBoxes && chars.map((c) => (
              <rect key={"b" + c.id} x={c.bbox[0]} y={c.bbox[1]} width={c.bbox[2] - c.bbox[0]} height={c.bbox[3] - c.bbox[1]}
                fill={selected === c.id ? "rgba(37,99,235,0.15)" : "rgba(251,146,60,0.16)"}
                stroke={selected === c.id ? "#2563eb" : "#fb923c"} strokeWidth={strokeW} />
            ))}
            {!eyeOn && chars.map((c) => {
              const bx = c.bx ?? c.bbox[2] + R * 1.5, by = c.by ?? c.bbox[1] - R;
              const [tx, ty] = nearestOnBox(bx, by, c.bbox);
              const gc = groupColor(c);
              const col = gc || (c.check && !c.verified ? "#ea580c" : "#2563eb");
              const sel = selected === c.id;
              return (
                <g key={"l" + c.id}>
                  <line x1={bx} y1={by} x2={tx} y2={ty} stroke={col} strokeWidth={strokeW * 0.8} />
                  <circle data-bid={c.id} cx={bx} cy={by} r={R} fill={sel ? col : "#ffffff"} stroke={col} strokeWidth={strokeW * 1.3}
                    style={{ cursor: tool === "select" ? "move" : "inherit" }} />
                  <text x={bx} y={by + R * 0.36} textAnchor="middle" fontSize={R * 1.0} fontWeight="700"
                    fill={sel ? "#ffffff" : col} style={{ pointerEvents: "none" }}>{c.no}</text>
                </g>
              );
            })}
            {/* pending (eye) */}
            {eyeOn && pending.map((p) => (
              <rect key={p.id} x={p.bbox[0]} y={p.bbox[1]} width={p.bbox[2] - p.bbox[0]} height={p.bbox[3] - p.bbox[1]}
                fill="rgba(22,163,74,0.10)" stroke="#16a34a" strokeDasharray={`${strokeW * 4} ${strokeW * 3}`} strokeWidth={strokeW * 1.5} />
            ))}
            {rubber && (
              <rect x={Math.min(rubber.x0, rubber.x1)} y={Math.min(rubber.y0, rubber.y1)} width={Math.abs(rubber.x1 - rubber.x0)}
                height={Math.abs(rubber.y1 - rubber.y0)} fill="rgba(37,99,235,0.08)" stroke="#2563eb"
                strokeDasharray={`${strokeW * 4} ${strokeW * 3}`} strokeWidth={strokeW * 1.5} />
            )}
          </svg>
        </div>

        {/* top-left: home + engine chip + warnings */}
        <div className="absolute left-3 top-3 flex flex-col gap-2" onMouseDown={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="flex items-center gap-1 rounded-md border bg-white px-3 py-1.5 text-sm shadow-sm hover:bg-gray-50">
              <Icon d="home" size={16} /> Home
            </button>
            {doc.engine_label && (
              <span title={doc.engine_why || ""} className="rounded-full border bg-white px-3 py-1 text-xs text-gray-600 shadow-sm">
                ran: <b>{doc.engine_label}</b>
              </span>
            )}
            <span className={`text-xs ${saveState === "error" ? "text-red-600" : "text-gray-400"}`}>
              {saveState === "saving" ? "Saving..." : saveState === "dirty" ? "Unsaved" : saveState === "error" ? "Save failed" : "Saved"}
            </span>
          </div>
          {(doc.warnings || []).map((w, i) => (
            <div key={i} className="max-w-xl rounded-md border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-xs text-yellow-800 shadow-sm">⚠ {w}</div>
          ))}
          {busy && <div className="rounded-md bg-white px-3 py-1.5 text-xs shadow">{busy}</div>}
        </div>

        {/* top-right: zoom */}
        <div className="absolute right-3 top-3 flex items-center rounded-md border bg-white shadow-sm" onMouseDown={(e) => e.stopPropagation()}>
          <button className="px-2 py-1.5 hover:bg-gray-50" onClick={() => zoomAt(1 / 1.2)}><Icon d="minus" size={16} /></button>
          <span className="w-14 border-x text-center text-sm">{Math.round(zoom * 100)}%</span>
          <button className="px-2 py-1.5 hover:bg-gray-50" onClick={() => zoomAt(1.2)}><Icon d="plus" size={16} /></button>
          <button className="border-l px-2 py-1.5 hover:bg-gray-50" title="Fit (F)" onClick={fit}><Icon d="fit" size={16} /></button>
        </div>

        {/* eye bar */}
        {eyeOn && (
          <div className="absolute left-1/2 top-3 z-20 w-[520px] -translate-x-1/2" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between rounded-lg bg-gray-900 px-3 py-2 text-white shadow-lg">
              <span className="text-xs text-gray-300">{chars.length} detected hidden · drag a box over text that is still visible</span>
              <div className="flex gap-2">
                <button onClick={eyeFinish} className="flex items-center gap-1 rounded bg-green-600 px-3 py-1 text-sm font-semibold hover:bg-green-500">
                  <Icon d="check" size={15} /> Finish
                </button>
                <button onClick={eyeCancel} className="rounded bg-gray-700 px-3 py-1 text-sm hover:bg-gray-600">Cancel <span className="ml-1 rounded bg-red-600 px-1 text-[10px]">Esc</span></button>
              </div>
            </div>
            {pending.length > 0 && (
              <div className="mt-2 max-h-56 overflow-auto rounded-lg border bg-white p-2 shadow-lg">
                {pending.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-2 py-1">
                    <span className="w-6 text-xs text-gray-400">#{i + 1}</span>
                    {p.loading ? <span className="text-xs text-gray-500">Reading...</span> : (
                      <input value={p.text} placeholder={p.error ? "could not read - type it" : "type value"}
                        onChange={(e) => setPending((ps) => ps.map((q) => (q.id === p.id ? { ...q, text: e.target.value, parsed: null } : q)))}
                        className="flex-1 rounded border px-2 py-1 font-mono text-sm" />
                    )}
                    <button onClick={() => setPending((ps) => ps.filter((q) => q.id !== p.id))} className="text-gray-400 hover:text-red-600"><Icon d="x" size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* bottom toolbar (draggable) */}
        <div className="absolute z-10 flex items-center gap-1 rounded-lg bg-gray-900 px-2 py-1.5 text-white shadow-xl"
          style={dock.x === null ? { left: "50%", bottom: 18, transform: "translateX(-50%)" } : { left: dock.x, top: dock.y }}
          onMouseDown={(e) => e.stopPropagation()}>
          <span className="cursor-move px-1 text-gray-400" onMouseDown={dockDown}><Icon d="grip" /></span>
          <TB title="Select / move balloons (V)" on={tool === "select" && !eyeOn} onClick={() => setTool("select")} icon="box" />
          <TB title="Renumber in list order (N)" onClick={() => renumber()} icon="num" />
          <TB title="Add balloon manually (A)" on={tool === "add"} onClick={() => setTool(tool === "add" ? "select" : "add")} icon="pencil" />
          <TB title="Contrast" on={contrast} onClick={() => setContrast(!contrast)} icon="contrast" />
          <TB title="Sort & renumber in reading order" onClick={sortAndNumber} icon="sort" />
          <TB title="Re-detect balloons (runs extraction again)" onClick={async () => {
            if (!window.confirm("Run extraction again? Manual edits on this drawing will be replaced.")) return;
            setBusy("Re-detecting..."); try { await api.rerun(docId); await load(); } catch (e) { alert(e.message || e); } setBusy("");
          }} icon="detect" />
          <TB title="Rotate view 90°" onClick={() => setRot((r) => (r + 90) % 360)} icon="rotate" />
          <TB title="Hide detected dimensions (H)" on={eyeOn} danger={eyeOn} onClick={() => (eyeOn ? eyeCancel() : eyeStart())} icon={eyeOn ? "eye" : "eyeOff"} />
        </div>

        {/* inspection plan button */}
        <button onMouseDown={(e) => e.stopPropagation()} onClick={() => setPlanOpen(true)}
          className="absolute bottom-4 right-4 flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50">
          <Icon d="plan" size={16} /> Inspection Plan <span className="text-gray-400">{chars.length}</span>
        </button>
      </div>

      {/* ------------------------------------------------ right panel */}
      <div className="flex w-[360px] flex-col border-l bg-white">
        <div className="flex items-center gap-2 border-b p-3">
          <div className="relative flex-1">
            <button onClick={() => setModeOpen(!modeOpen)} className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-gray-50">
              <span className="flex items-center gap-2"><Icon d={{ balloon: "balloon", title: "title", grid: "grid", groups: "group" }[mode]} size={16} />
                {{ balloon: "Ballooning", title: "Title Block", grid: "Reference Grid", groups: "Dimension Groups" }[mode]}</span>
              <Icon d="chevron" size={14} />
            </button>
            {modeOpen && (
              <div className="absolute z-30 mt-1 w-full rounded-md border bg-white py-1 shadow-lg">
                {[["balloon", "Ballooning", "balloon"], ["title", "Title Block", "title"], ["grid", "Reference Grid", "grid"], ["groups", "Dimension Groups", "group"]].map(([k, l, ic]) => (
                  <button key={k} onClick={() => { setMode(k); setModeOpen(false); }} className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${mode === k ? "bg-gray-100" : ""}`}>
                    <Icon d={ic} size={16} /> {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <button onClick={() => setSettingsOpen(!settingsOpen)} className="rounded-md border p-2 hover:bg-gray-50"><Icon d="gear" size={16} /></button>
            {settingsOpen && (
              <div className="absolute right-0 z-30 mt-1 w-60 rounded-md border bg-white p-3 text-sm shadow-lg">
                <label className="flex items-center justify-between py-1">Show detected boxes <input type="checkbox" checked={showBoxes} onChange={(e) => setShowBoxes(e.target.checked)} /></label>
                <label className="block py-1">Balloon size
                  <input type="range" min="0.5" max="2.5" step="0.1" value={balloonScale} onChange={(e) => setBalloonScale(Number(e.target.value))} className="w-full" /></label>
                <label className="flex items-center justify-between py-1">Show grid in Ballooning
                  <input type="checkbox" checked={!!grid?.show} disabled={!grid} onChange={(e) => setGrid({ ...grid, show: e.target.checked })} /></label>
              </div>
            )}
          </div>
        </div>

        {mode === "balloon" && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="space-y-2 border-b p-3">
              <a href={api.pdfUrl(docId)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-md bg-red-600 py-2.5 font-semibold text-white hover:bg-red-700">
                <Icon d="download" size={17} /> Download PDF
              </a>
              <div className="relative flex">
                <a href={api.sheetUrl(docId, "xlsx")} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-l-md border py-2 text-sm font-semibold hover:bg-gray-50">
                  <Icon d="sheet" size={16} /> Inspection Sheet
                </a>
                <button onClick={() => setSheetOpen(!sheetOpen)} className="rounded-r-md border border-l-0 px-3 hover:bg-gray-50"><Icon d="chevron" size={14} /></button>
                {sheetOpen && (
                  <div className="absolute right-0 top-full z-30 mt-1 w-44 rounded-md border bg-white py-1 text-sm shadow-lg">
                    {[["xlsx", "Excel (.xlsx)"], ["csv", "CSV"], ["pdf", "PDF"]].map(([f, l]) => (
                      <a key={f} href={api.sheetUrl(docId, f)} target="_blank" rel="noreferrer" onClick={() => setSheetOpen(false)} className="block px-3 py-2 hover:bg-gray-50">{l}</a>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-1 border-b bg-gray-50 p-2">
              {[["dims", "Dims.", chars.length], ["views", "Views", views.length], ["tols", "Tols.", null]].map(([k, l, n]) => (
                <button key={k} onClick={() => setTab(k)} className={`flex flex-1 items-center justify-center gap-1 rounded-md py-1.5 text-sm ${tab === k ? "bg-white font-semibold shadow-sm" : "text-gray-500 hover:text-gray-800"}`}>
                  {l} {n !== null && <span className="rounded bg-gray-200 px-1.5 text-[11px] text-gray-600">{n}</span>}
                </button>
              ))}
            </div>

            {tab === "dims" && (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex items-center gap-2 px-3 pt-3 text-sm">
                  <span className="text-gray-500">Units</span>
                  <select value={units} onChange={(e) => { setUnits(e.target.value); setConvert(false); }} className="rounded-md border px-2 py-1">
                    <option value="mm">mm</option><option value="inch">inch</option>
                  </select>
                  <button onClick={() => setConvert(!convert)} className={`ml-auto rounded-md border px-2 py-1 ${convert ? "bg-blue-50 text-blue-700" : "text-gray-500"}`}>Convert</button>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 text-sm">
                  <div className="h-1.5 flex-1 overflow-hidden rounded bg-gray-200">
                    <div className="h-full bg-green-600" style={{ width: `${chars.length ? (100 * verifiedCount) / chars.length : 0}%` }} />
                  </div>
                  <span className="whitespace-nowrap">{verifiedCount}/{chars.length} verified</span>
                  <div className="relative">
                    <button onClick={() => setFilterOpen(!filterOpen)} className={`rounded p-1 ${filter !== "all" ? "text-blue-600" : "text-gray-500"}`}><Icon d="filter" size={16} /></button>
                    {filterOpen && (
                      <div className="absolute right-0 z-30 mt-1 w-44 rounded-md border bg-white py-1 shadow-lg">
                        {[["all", "All"], ["review", "Needs review"], ["unverified", "Not verified"], ["verified", "Verified"], ["gdt", "GD&T only"], ["basic", "BASIC only"]].map(([k, l]) => (
                          <button key={k} onClick={() => { setFilter(k); setFilterOpen(false); }} className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${filter === k ? "font-semibold" : ""}`}>{l}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="min-h-0 flex-1 space-y-1.5 overflow-auto px-3 pb-3">
                  {visibleRows.map((c) => (
                    <DimRow key={c.id} c={c} sel={selected === c.id} units={convert ? units : "mm"} editing={editing === c.id}
                      grid={gridCell(c)}
                      onClick={() => focusChar(c)} onToggle={() => update(c.id, { verified: !c.verified })}
                      onRemove={() => remove(c.id)} onEdit={() => setEditing(c.id)}
                      onSave={(text) => { update(c.id, { text, check: c.source === "manual" && !text ? c.check : c.check }); setEditing(null); }} />
                  ))}
                  {!visibleRows.length && <div className="py-6 text-center text-sm text-gray-400">Nothing here</div>}
                </div>
              </div>
            )}

            {tab === "views" && (
              <div className="min-h-0 flex-1 space-y-2 overflow-auto p-3">
                <div className="flex gap-2">
                  <button onClick={() => setTool("view")} className={`flex flex-1 items-center justify-center gap-1 rounded-md py-2 text-sm font-semibold text-white ${tool === "view" ? "bg-red-700" : "bg-red-500 hover:bg-red-600"}`}>
                    <Icon d="plus" size={15} /> {tool === "view" ? "Draw a box on the drawing" : "Add View"}
                  </button>
                </div>
                {views.map((v) => (
                  <div key={v.id} onClick={() => setSelView(v.id)}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm ${selView === v.id ? "border-blue-500 bg-blue-50" : "hover:bg-gray-50"}`}>
                    <input value={v.name} onChange={(e) => {
                      const old = v.name, nn = e.target.value;
                      setViews((vs) => vs.map((x) => (x.id === v.id ? { ...x, name: nn } : x)));
                      setChars((cs) => cs.map((c) => (c.view === old ? { ...c, view: nn } : c)));
                    }} className="flex-1 bg-transparent outline-none" />
                    <span className="text-xs text-gray-400">{chars.filter((c) => c.view === v.name).length}</span>
                    <button onClick={(e) => { e.stopPropagation(); setViews((vs) => vs.filter((x) => x.id !== v.id)); }} className="text-gray-400 hover:text-red-600"><Icon d="trash" size={15} /></button>
                  </div>
                ))}
                {!views.length && <div className="py-6 text-center text-sm text-gray-400">No views yet</div>}
              </div>
            )}

            {tab === "tols" && (
              <div className="min-h-0 flex-1 overflow-auto p-3 text-sm">
                <label className="mb-3 block">
                  <span className="text-gray-500">General tolerance (applies to dims without own tolerance)</span>
                  <input value={generalTol} onChange={(e) => setGeneralTol(e.target.value)} placeholder="e.g. ISO 2768-m / ±0.2"
                    className="mt-1 w-full rounded-md border px-2 py-1.5" />
                </label>
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500"><th className="py-1">#</th><th>Value</th><th>Lower</th><th>Upper</th></tr></thead>
                  <tbody>
                    {numbered.filter((c) => c.plus !== null && c.plus !== undefined || c.type === "gdt").map((c) => (
                      <tr key={c.id} className="cursor-pointer border-t hover:bg-gray-50" onClick={() => focusChar(c)}>
                        <td className="py-1.5 font-semibold text-red-600">{c.no}</td>
                        <td className="font-mono">{c.text}</td>
                        <td>{c.type === "gdt" ? "—" : fmtNum(c.lower, convert ? units : "mm")}</td>
                        <td>{c.type === "gdt" ? fmtNum(c.plus, convert ? units : "mm") : fmtNum(c.upper, convert ? units : "mm")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {mode === "title" && (
          <div className="min-h-0 flex-1 overflow-auto p-3">
            <button onClick={() => setTool("title")} className={`mb-3 flex w-full items-center justify-center gap-2 rounded-md py-2 text-sm font-semibold text-white ${tool === "title" ? "bg-red-800" : "bg-red-600 hover:bg-red-700"}`}>
              <Icon d="fit" size={16} /> {tool === "title" ? "Draw a box around the title block" : titleBlock ? "Re-select title block region" : "Select title block region"}
            </button>
            {titleBlock?.error && <div className="mb-2 text-xs text-red-600">{titleBlock.error}</div>}
            <div className="mb-2 font-semibold">Detected fields</div>
            {(titleBlock?.fields || []).map((f, i) => (
              <div key={f.key || i} className="mb-2 rounded-md border p-3">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  {f.label || f.key}
                  <input type="checkbox" checked={!!f.checked} onChange={(e) => setTitleBlock((t) => ({ ...t, fields: t.fields.map((x, j) => (j === i ? { ...x, checked: e.target.checked } : x)) }))} />
                </div>
                <input value={f.value || ""} onChange={(e) => setTitleBlock((t) => ({ ...t, fields: t.fields.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) }))}
                  className="mt-1 w-full rounded border-none bg-transparent p-0 text-sm font-medium outline-none" />
              </div>
            ))}
            {!titleBlock?.fields?.length && <div className="text-sm text-gray-400">Select the title block region to read Part Name, Part Number, Material, Organization, Author, Scale, Date, Model ...</div>}
          </div>
        )}

        {mode === "grid" && (
          <div className="min-h-0 flex-1 overflow-auto p-3 text-sm">
            {!grid ? (
              <div className="py-10 text-center">
                <div className="text-base font-semibold text-gray-700">No reference grid configured</div>
                <div className="mt-1 text-gray-500">Add a grid overlay to help locate dimensions on the drawing</div>
                <button onClick={autoGrid} className="mx-auto mt-5 flex items-center gap-2 rounded-md bg-red-600 px-5 py-2.5 font-semibold text-white hover:bg-red-700"><Icon d="detect" size={16} /> Auto-Detect</button>
                <button onClick={() => { autoGrid(); }} className="mx-auto mt-3 flex items-center gap-2 rounded-md border px-5 py-2.5 hover:bg-gray-50"><Icon d="grid" size={16} /> Create Grid</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-md border p-3">
                  <div className="mb-2 text-xs font-semibold tracking-wide text-gray-500">LABEL SCHEME</div>
                  <label className="flex items-center justify-between py-1">Row start ↓ <input value={grid.rowStart} maxLength={1} onChange={(e) => setGrid({ ...grid, rowStart: e.target.value.toUpperCase() || "A" })} className="w-14 rounded border px-2 py-1 text-center" /></label>
                  <label className="flex items-center justify-between py-1">Column start → <input type="number" value={grid.colStart} onChange={(e) => setGrid({ ...grid, colStart: Number(e.target.value) || 1 })} className="w-14 rounded border px-2 py-1 text-center" /></label>
                  <label className="flex items-center justify-between py-1">Rows <input type="number" value={grid.ys.length + 1} onChange={(e) => setGridCount("ys", e.target.value)} className="w-14 rounded border px-2 py-1 text-center" /></label>
                  <label className="flex items-center justify-between py-1">Columns <input type="number" value={grid.xs.length + 1} onChange={(e) => setGridCount("xs", e.target.value)} className="w-14 rounded border px-2 py-1 text-center" /></label>
                </div>
                <div className="rounded-md border p-2">
                  <div className="mb-1 px-1 text-xs text-gray-500">Lines (drag on the drawing to move)</div>
                  {[...grid.ys.map((v, i) => ["ys", i, `Row line ${i + 1}`]), ...grid.xs.map((v, i) => ["xs", i, `Column line ${i + 1}`])].map(([ax, i, l]) => {
                    const key = `${ax}:${i}`, locked = (grid.locked || []).includes(key);
                    return (
                      <div key={key} className="flex items-center gap-2 px-1 py-1">
                        <span className="flex-1">{l}</span>
                        <button title="Lock" onClick={() => setGrid({ ...grid, locked: locked ? grid.locked.filter((k) => k !== key) : [...(grid.locked || []), key] })} className={locked ? "text-gray-900" : "text-gray-300"}><Icon d="lock" size={15} /></button>
                        <button title="Delete line" onClick={() => setGrid({ ...grid, [ax]: grid[ax].filter((_, j) => j !== i), locked: [] })} className="text-red-500"><Icon d="x" size={15} /></button>
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => setGrid(null)} className="flex w-full items-center justify-center gap-2 rounded-md border py-2 hover:bg-gray-50"><Icon d="trash" size={15} /> Remove Grid</button>
                <div className="rounded-md bg-blue-50 p-3 text-blue-800">The grid location for every item will be shown in the inspection plan.</div>
                <button onClick={() => setMode("balloon")} className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 py-2.5 font-semibold text-white hover:bg-blue-700">‹ Back to Ballooning</button>
              </div>
            )}
          </div>
        )}

        {mode === "groups" && (
          <div className="flex min-h-0 flex-1 flex-col p-3 text-sm">
            <button onClick={() => {
              const g = { id: uid(), name: `Group ${groups.length + 1}`, color: GROUP_COLORS[groups.length % GROUP_COLORS.length] };
              setGroups((gs) => [...gs, g]); setActiveGroup(g.id);
            }} className="mb-3 flex w-full items-center justify-center gap-2 rounded-md border py-2 hover:bg-gray-50"><Icon d="plus" size={15} /> Create Group</button>
            {groups.map((g) => (
              <div key={g.id} onClick={() => setActiveGroup(g.id)} className={`mb-1.5 flex items-center gap-2 rounded-md border px-2 py-1.5 ${activeGroup === g.id ? "border-blue-500 bg-blue-50" : ""}`}>
                <span className="h-3 w-3 rounded-full" style={{ background: g.color }} />
                <input value={g.name} onChange={(e) => setGroups((gs) => gs.map((x) => (x.id === g.id ? { ...x, name: e.target.value } : x)))} className="flex-1 bg-transparent outline-none" />
                <span className="text-xs text-gray-400">{chars.filter((c) => c.group === g.id).length}</span>
                <button onClick={(e) => { e.stopPropagation(); setGroups((gs) => gs.filter((x) => x.id !== g.id)); setChars((cs) => cs.map((c) => (c.group === g.id ? { ...c, group: null } : c))); }} className="text-gray-400 hover:text-red-600"><Icon d="trash" size={14} /></button>
              </div>
            ))}
            <div className="mb-1 mt-3 text-xs font-semibold text-gray-500">All Dimensions {activeGroup ? "— tick to add to the selected group" : ""}</div>
            <div className="min-h-0 flex-1 space-y-1 overflow-auto">
              {numbered.map((c) => {
                const g = groups.find((x) => x.id === c.group);
                return (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 hover:bg-gray-50" onMouseEnter={() => setSelected(c.id)}>
                    <input type="checkbox" disabled={!activeGroup} checked={!!activeGroup && c.group === activeGroup}
                      onChange={(e) => update(c.id, { group: e.target.checked ? activeGroup : null })} />
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">{c.no}</span>
                    <span className="flex-1 font-mono">{c.text}</span>
                    {g && <span className="rounded px-1.5 text-[11px] text-white" style={{ background: g.color }}>{g.name}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ------------------------------------------------ inspection plan drawer */}
      {planOpen && (
        <div className="fixed inset-0 z-40 flex items-end bg-black/30" onClick={() => setPlanOpen(false)}>
          <div className="max-h-[70vh] w-full overflow-auto rounded-t-xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div className="text-lg font-semibold">Inspection Plan <span className="text-gray-400">({chars.length})</span></div>
              <div className="flex gap-2">
                <a href={api.sheetUrl(docId, "xlsx")} target="_blank" rel="noreferrer" className="rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50">Export Excel</a>
                <button onClick={() => setPlanOpen(false)} className="rounded-md border p-1.5"><Icon d="x" size={16} /></button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                {["#", "Grid", "View", "Characteristic", "Type", "Nominal", "Lower", "Upper", "Qty", "Basic", "Group", "Verified", "Check"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}
              </tr></thead>
              <tbody>
                {numbered.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-gray-50">
                    <td className="px-2 py-1.5 font-semibold text-red-600">{c.no}</td>
                    <td className="px-2">{gridCell(c) || "—"}</td>
                    <td className="px-2">{c.view || "—"}</td>
                    <td className="px-2 font-mono">{c.text}</td>
                    <td className="px-2">{c.gdt || c.type}</td>
                    <td className="px-2">{fmtNum(c.nominal, convert ? units : "mm")}</td>
                    <td className="px-2">{fmtNum(c.lower, convert ? units : "mm")}</td>
                    <td className="px-2">{fmtNum(c.upper, convert ? units : "mm")}</td>
                    <td className="px-2">{c.qty || "—"}</td>
                    <td className="px-2">{c.basic ? "BASIC" : ""}</td>
                    <td className="px-2">{(groups.find((g) => g.id === c.group) || {}).name || ""}</td>
                    <td className="px-2">{c.verified ? "✓" : ""}</td>
                    <td className="max-w-[240px] truncate px-2 text-xs text-orange-700" title={c.check || ""}>{c.check || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ toolbar button */
function TB({ icon, onClick, on, danger, title }) {
  return (
    <button title={title} onClick={onClick}
      className={`rounded-md p-1.5 ${danger ? "bg-red-600" : on ? "bg-white/20" : "hover:bg-white/10"}`}>
      <Icon d={icon} size={20} />
    </button>
  );
}

/* ------------------------------------------------------------------ dims row */
function DimRow({ c, sel, units, editing, grid, onClick, onToggle, onRemove, onEdit, onSave }) {
  const [txt, setTxt] = useState(c.text || "");
  useEffect(() => setTxt(c.text || ""), [c.text]);
  const review = c.check && !c.verified;
  return (
    <div onClick={onClick} onDoubleClick={onEdit}
      className={`group rounded-md border px-2 py-2 ${sel ? "border-blue-500 bg-blue-50" : "hover:bg-gray-50"}`}>
      <div className="flex items-center gap-2">
        <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-red-600 px-1 text-xs font-bold text-white">{c.no}</span>
        {editing ? (
          <input autoFocus value={txt} onChange={(e) => setTxt(e.target.value)} onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === "Enter") onSave(txt); if (e.key === "Escape") onSave(c.text || ""); }}
            onBlur={() => onSave(txt)} className="flex-1 rounded border px-1 py-0.5 font-mono text-sm" />
        ) : (
          <span className="flex-1 truncate text-center font-mono text-sm" title={c.check || ""}>{c.text || <i className="text-gray-400">double-click to type</i>}</span>
        )}
        {c.basic && <span className="rounded bg-indigo-100 px-1 text-[10px] font-semibold text-indigo-700">BASIC</span>}
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} title={c.verified ? "Verified" : review ? c.check : "Mark verified"}
          className={c.verified ? "text-green-600" : review ? "text-orange-500" : "text-gray-300 hover:text-green-600"}>
          <Icon d={c.verified ? "check" : review ? "alert" : "check"} size={18} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-gray-300 opacity-0 hover:text-red-600 group-hover:opacity-100"><Icon d="x" size={15} /></button>
      </div>
      {sel && (
        <div className="mt-1.5 grid grid-cols-3 gap-x-2 gap-y-0.5 pl-8 text-[11px] text-gray-500">
          <span>type: <b className="text-gray-700">{c.gdt || c.type}</b></span>
          <span>nom: <b className="text-gray-700">{fmtNum(c.nominal, units)}</b></span>
          <span>qty: <b className="text-gray-700">{c.qty || "—"}</b></span>
          <span>min: <b className="text-gray-700">{fmtNum(c.lower, units)}</b></span>
          <span>max: <b className="text-gray-700">{fmtNum(c.upper, units)}</b></span>
          <span>grid: <b className="text-gray-700">{grid || "—"}</b></span>
          {c.view && <span className="col-span-3">view: {c.view}</span>}
          {c.check && <span className="col-span-3 text-orange-700">⚠ {c.check}</span>}
        </div>
      )}
    </div>
  );
}
